import { cn } from "@/lib/studio/utils";
import type { RunStatusValue } from "@/services/studio/types";

export interface RunStatusBadgeProps {
  status: RunStatusValue | string;
  className?: string;
}

/**
 * Tone map for a run's terminal (or in-flight) status.
 *
 * Three tones, drawn from the existing design tokens — the same three the artifact
 * validation badge uses (see `lib/studio/validation.ts`):
 *
 *  - success     `done` — the run produced what it promised.
 *  - destructive `error` / `failed` — the run did NOT produce it. This tone used to be
 *                the same neutral grey as `cached` and `running`, so a failed run was
 *                visually indistinguishable from a cheap cache hit.
 *  - warm        `partial` / `input_required` / `action_blocked` — the run stopped short
 *                and needs a human. Not a failure, and emphatically not a success; the
 *                caution token is the honest middle.
 *
 * The four statuses below `error` are all written by the live playbook runner
 * (ncoder-playground `app/runner/execution.py`) and previously fell through to the
 * `queued` grey with a raw `action_blocked` label.
 */
const STYLES: Record<string, string> = {
  queued: "bg-muted text-muted-foreground border-border",
  running: "bg-muted text-foreground border-border",
  done: "bg-success/15 text-success border-transparent",
  cached: "bg-muted text-foreground border-border",
  error: "bg-destructive/15 text-destructive border-destructive/30",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  partial: "bg-accent-warm-soft text-accent-warm border-transparent",
  input_required: "bg-accent-warm-soft text-accent-warm border-transparent",
  action_blocked: "bg-accent-warm-soft text-accent-warm border-transparent",
  cancelled: "bg-muted text-muted-foreground border-border",
  stopped: "bg-muted text-muted-foreground border-border",
};

/**
 * Wire values are snake_case machine codes. The badge renders uppercase, so the only
 * work here is turning `action_blocked` into words a person reads without decoding.
 */
const LABELS: Record<string, string> = {
  input_required: "input needed",
  action_blocked: "action blocked",
  partial: "partial",
  failed: "failed",
};

/** Title text for the statuses whose one word does not say enough on its own. */
const TITLES: Record<string, string> = {
  partial: "The run finished, but did not produce everything it set out to.",
  input_required: "The run stopped because a required input was missing.",
  action_blocked: "The run produced a result, but the action it wanted to take was blocked.",
  error: "The run did not produce a usable result.",
  failed: "The run did not produce a usable result.",
};

export function RunStatusBadge({ status, className }: RunStatusBadgeProps) {
  const cls = STYLES[status] ?? STYLES.queued;
  const label = LABELS[status] ?? status;
  return (
    <span
      title={TITLES[status]}
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wide",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}
